/* GSTIN cart attribute logic for cart page + cart drawer
   - Uppercase + sanitize everywhere (load, input, blur, paste, submit, checkout click)
   - Full checksum validation (ISO-7064 mod-36)
*/

(function GSTINEnhancer(){
  // --- GSTIN validation with checksum ---
  function isValidGSTIN(gstin) {
    if (!gstin || gstin.length !== 15) return false;
    gstin = gstin.toUpperCase();

    // Basic format: 01–37 state, PAN, entity, Z, checksum
    const format = /^([0][1-9]|[1-2][0-9]|3[0-7])[A-Z]{5}[0-9]{4}[A-Z][A-Z0-9]Z[A-Z0-9]$/;
    if (!format.test(gstin)) return false;

    // Map 0–9, A–Z -> 0..35
    const codeVal = (c) => {
      const cp = c.charCodeAt(0);
      if (cp >= 48 && cp <= 57) return cp - 48; // 0-9
      if (cp >= 65 && cp <= 90) return cp - 55; // A=10..Z=35
      return -1;
    };

    // Compute checksum (first 14 chars), alternating weights 1,2 in base-36
    let sum = 0;
    for (let i = 0; i < 14; i++) {
      const ci = codeVal(gstin[i]);
      if (ci < 0) return false;
      const weight = (i % 2) + 1;         // 1,2,1,2...
      const prod = ci * weight;
      sum += Math.floor(prod / 36) + (prod % 36); // base-36 digital sum
    }
    const checkCode = (36 - (sum % 36)) % 36;
    const checkChar = checkCode < 10
      ? String.fromCharCode(48 + checkCode)
      : String.fromCharCode(55 + checkCode);      // 10->'A'

    return gstin[14] === checkChar;
  }

  // --- helpers ---
  const clean = (s) => (s || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 15);

  function showErrorFor(input, msg) {
    const block = input.closest('[data-gstin-block]');
    const err = block && block.querySelector('.gstin-error');
    if (err) {
      err.textContent = msg;
      err.hidden = false;
    }
    input.setAttribute('aria-invalid', 'true');
  }
  function clearErrorFor(input) {
    const block = input.closest('[data-gstin-block]');
    const err = block && block.querySelector('.gstin-error');
    if (err) {
      err.textContent = '';
      err.hidden = true;
    }
    input.setAttribute('aria-invalid', 'false');
  }

  // --- UI logic for each form ---
  function enhanceForm(form) {
    if (!form || form.__gstinBound) return;
    form.__gstinBound = true;

    const blocks = form.querySelectorAll('[data-gstin-block]');
    blocks.forEach(block => {
      const toggle = block.querySelector('.gstin-switch');
      const fieldset = block.querySelector('.gstin-fieldset');
      const input = block.querySelector('[data-gstin-input]');
      if (!toggle || !fieldset || !input) return;

      // sanitize immediately (fixes legacy dirty values)
      input.value = clean(input.value);

      const setOpen = (open) => {
        fieldset.classList.toggle('is-open', !!open);
        if (toggle) toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      };

      // init open state
      setOpen(!!input.value);

      // toggle behaviour
      toggle.addEventListener('change', () => {
        setOpen(toggle.checked);
        if (!toggle.checked) {
          input.value = '';
          clearErrorFor(input);
        }
      });

      // input handlers: sanitize + validate
      const validate = () => {
        input.value = clean(input.value);
        if (input.value && !isValidGSTIN(input.value)) {
          showErrorFor(input, 'Enter a valid 15-character GSTIN (checksum failed)');
        } else {
          clearErrorFor(input);
        }
      };

      input.addEventListener('input', validate);
      ['change','blur','paste'].forEach(ev => input.addEventListener(ev, validate));

      // submit guard (some themes still submit the form)
      form.addEventListener('submit', (e) => {
        input.value = clean(input.value); // final sanitize
        if (toggle.checked && input.value && !isValidGSTIN(input.value)) {
          e.preventDefault();
          showErrorFor(input, 'Enter a valid GSTIN (checksum failed)');
          input.focus();
        }
      });
    });
  }

  // Bind to all cart forms (page + drawer)
  function bindAll() {
    document.querySelectorAll('form[action*="/cart"], #CartDrawer-Form')
      .forEach(enhanceForm);
  }

  // Catch checkout buttons that bypass native submit (drawer/AJAX flows)
  function bindCheckoutClickSanitizer() {
    if (window.__gstinCheckoutBound) return;
    window.__gstinCheckoutBound = true;

    document.addEventListener('click', (e) => {
      const el = e.target && e.target.closest(
        'button[name="checkout"], [data-checkout-button], .cart__checkout-button, button#checkout, a[href*="/checkout"]'
      );
      if (!el) return;

      const form = el.closest('form') || document.querySelector('#CartDrawer-Form, form[action*="/cart"]');
      if (!form) return;

      const input = form.querySelector('[data-gstin-input]');
      const toggle = form.querySelector('.gstin-switch');
      if (!input) return;

      input.value = clean(input.value); // final sanitize

      // Optional: if opted in and invalid, block and show error
      if (toggle && toggle.checked && input.value && !isValidGSTIN(input.value)) {
        e.preventDefault();
        showErrorFor(input, 'Enter a valid GSTIN (checksum failed)');
        input.focus();
      }
    });
  }

  // Observe DOM mutations (drawer re-renders)
  const observer = new MutationObserver(() => bindAll());
  observer.observe(document.documentElement, { childList: true, subtree: true });

  // Initial run
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      bindAll();
      bindCheckoutClickSanitizer();
    });
  } else {
    bindAll();
    bindCheckoutClickSanitizer();
  }

  // Rebind on common Dawn events
  window.addEventListener('cart:updated', bindAll);
  window.addEventListener('cart-drawer:open', bindAll);
})();